import { firebaseApp } from "../config/firebase-config";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  getDoc,
  setDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

const FIRESTORE_DB_ID = (process.env.REACT_APP_FIRESTORE_DB_ID || "(default)").trim();
const STORAGE_BUCKET = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "";
const PRODUCTS_COLLECTION =
  (process.env.REACT_APP_PRODUCTS_COLLECTION || "products").trim() || "products";

const initFirestore = () => {
  try {
    if (FIRESTORE_DB_ID && FIRESTORE_DB_ID !== "(default)") {
      return getFirestore(firebaseApp, FIRESTORE_DB_ID);
    }
    return getFirestore(firebaseApp);
  } catch (error) {
    console.warn(
      `[firebase] Could not initialize Firestore database "${FIRESTORE_DB_ID}", falling back to default.`,
      error
    );
    return getFirestore(firebaseApp);
  }
};

const initStorage = () => {
  try {
    if (STORAGE_BUCKET) {
      return getStorage(firebaseApp, `gs://${STORAGE_BUCKET}`);
    }
    return getStorage(firebaseApp);
  } catch (error) {
    console.warn(
      `[firebase] Could not initialize Storage bucket "${STORAGE_BUCKET || "(default)"}", falling back to default bucket.`,
      error
    );
    return getStorage(firebaseApp);
  }
};

const firestore = initFirestore();
const storage = initStorage();
const productsCollection = collection(firestore, PRODUCTS_COLLECTION);

const normalizeProductDoc = (snapshot) => {
  const data = snapshot.data() || {};
  const materials =
    Array.isArray(data.materials) && data.materials.length
      ? data.materials
          .map((m) => {
            if (!m) return null;
            const id = (m.id ?? m).toString?.().trim?.() || "";
            if (!id) return null;
            const qtyRaw = Number(m.qty ?? 1);
            const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
            return { id, qty };
          })
          .filter(Boolean)
      : [];
  return {
    id: snapshot.id,
    name: data.name || "",
    price: typeof data.price === "number" ? data.price : Number(data.price) || 0,
    categoryId: data.categoryId || "osszes",
    imageUrl: data.imageUrl || "",
    imagePath: data.imagePath || "",
    description: data.description || "",
    materials,
    createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : data.createdAt || null,
    updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : data.updatedAt || null,
  };
};

const sanitizeProductPayload = (payload = {}) => {
  const name = payload.name?.toString().trim() || "";
  const categoryId = payload.categoryId?.toString().trim() || "osszes";
  const priceNumber = Number(payload.price);
  if (!name) throw new Error("A termék neve kötelező.");
  if (!categoryId) throw new Error("A kategória kötelező.");
  if (Number.isNaN(priceNumber) || priceNumber <= 0) {
    throw new Error("Az árnak pozitív számnak kell lennie.");
  }
  const imageUrl = payload.imageUrl?.toString().trim() || "";
  const imagePath =
    payload.imagePath?.toString().trim() ||
    (imageUrl.startsWith("gs://") || imageUrl.startsWith("uploads/") ? imageUrl : "");
  const materials =
    Array.isArray(payload.materials) && payload.materials.length
      ? payload.materials
          .map((m) => {
            if (!m) return null;
            const id = (m.id ?? m).toString?.().trim?.() || "";
            if (!id) return null;
            const qtyRaw = Number(m.qty ?? 1);
            const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
            return { id, qty };
          })
          .filter(Boolean)
      : [];
  return {
    name,
    categoryId,
    price: priceNumber,
    ...(imageUrl ? { imageUrl } : {}),
    ...(imagePath ? { imagePath } : {}),
    ...(materials.length ? { materials } : { materials: [] }),
  };
};

const toStoragePath = (input) => {
  if (!input) return "";
  if (input.startsWith("gs://")) {
    const withoutScheme = input.slice(5);
    const slashIndex = withoutScheme.indexOf("/");
    if (slashIndex === -1) return "";
    return withoutScheme.slice(slashIndex + 1);
  }
  return input.replace(/^\/+/, "");
};

export const subscribeToProducts = (onData, onError) => {
  try {
    const q = query(productsCollection, orderBy("name", "asc"));
    return onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map(normalizeProductDoc);
        onData?.(list);
      },
      (error) => {
        console.error("[firebase] Product subscription failed:", error);
        onError?.(error);
      }
    );
  } catch (error) {
    console.error("[firebase] Could not subscribe to products:", error);
    onError?.(error);
    return () => {};
  }
};

export const fetchProductsOnce = async () => {
  const q = query(productsCollection, orderBy("name", "asc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(normalizeProductDoc);
};

export const uploadProductImageFile = async (file) => {
  if (!file) throw new Error("Nincs kiválasztott fájl.");
  const safeName = file.name?.replace(/\s+/g, "-").toLowerCase() || "image";
  const uniqueSuffix = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const fullPath = `uploads/${uniqueSuffix}-${safeName}`;
  const storageRef = ref(storage, fullPath);
  const task = uploadBytesResumable(storageRef, file, file.type ? { contentType: file.type } : undefined);
  await new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      () => {},
      (err) => reject(err),
      () => resolve()
    );
  });
  const downloadURL = await getDownloadURL(storageRef);
  const gsUri = STORAGE_BUCKET ? `gs://${STORAGE_BUCKET}/${fullPath}` : "";
  return { downloadURL, storagePath: fullPath, gsUri };
};

export const createProductRecord = async (payload) => {
  const data = sanitizeProductPayload(payload);
  const docRef = doc(productsCollection);
  await setDoc(docRef, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const saved = await getDoc(docRef);
  return normalizeProductDoc(saved);
};

export const updateProductRecord = async (id, payload) => {
  if (!id) throw new Error("A termék azonosítója hiányzik.");
  const data = sanitizeProductPayload(payload);
  const docRef = doc(productsCollection, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
  const saved = await getDoc(docRef);
  return normalizeProductDoc(saved);
};

const deleteStorageAtPath = async (value) => {
  const storagePath = toStoragePath(value);
  if (!storagePath) return;
  try {
    await deleteObject(ref(storage, storagePath));
  } catch (error) {
    if (error.code !== "storage/object-not-found") {
      console.warn("[firebase] Failed to delete storage object:", error);
    }
  }
};

export const deleteProductRecord = async (id, options = {}) => {
  if (!id) throw new Error("A termék azonosítója hiányzik a törléshez.");
  const docRef = doc(productsCollection, id);
  let imagePath = options.imagePath;
  if (!imagePath) {
    const snapshot = await getDoc(docRef);
    imagePath = snapshot.data()?.imagePath;
  }
  await deleteDoc(docRef);
  if (imagePath) {
    await deleteStorageAtPath(imagePath);
  }
};
